/**
 * AgentStartButtons
 *
 * One-click "Start" buttons for the AI CLI agents (Claude Code, Codex) that
 * are actually installed on the machine, shown in the AI terminal context
 * bar's right-hand chip group. Lets a user who opens the AI terminal jump
 * straight into an agent session instead of facing a blank prompt.
 *
 * Claude Code and Codex are co-equal: identical styling, stable order
 * (Claude first, then Codex) driven purely by `terminal:detectedAgents()` —
 * whichever agents are found on the machine.
 */

import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import type { DetectedAgent } from '../../../types/electron';

type AgentStartButtonsProps = {
  /** Terminal session ID to write the agent's launch command into */
  sessionId: string;
};

export function AgentStartButtons({ sessionId }: AgentStartButtonsProps) {
  const [agents, setAgents] = useState<DetectedAgent[]>([]);
  const { track } = useAnalytics();
  // Guards against a double-click sending the launch command twice (and
  // double-firing analytics) — set while a write is outstanding, cleared
  // once it settles either way.
  const writeInFlightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    window.electronAPI?.terminal
      ?.detectedAgents?.()
      .then((detected) => {
        if (!cancelled) setAgents(detected);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (agents.length === 0) {
    return null;
  }

  const handleStart = (agent: DetectedAgent) => async (e: React.MouseEvent) => {
    // The collapsed context bar is itself a <button> that toggles the
    // expanded details panel — stop the click from bubbling into it, same
    // pattern as the nested PRO badge button above.
    e.stopPropagation();

    // A write for this session is already outstanding — ignore the extra
    // click instead of sending the launch command (and analytics) twice.
    if (writeInFlightRef.current) {
      return;
    }
    writeInFlightRef.current = true;

    track('feature:ai_terminal_start_click', { agent: agent.id });

    try {
      // origin: 'app' tells the main-process tracker this write isn't the
      // user typing — a successful write fires 'source: button' below, so
      // the tracker must not also fire 'source: typed' for the same
      // keystroke.
      const result = await window.electronAPI?.terminal?.write({
        sessionId,
        data: `${agent.command}\r`,
        origin: 'app',
      });
      // Only report the activation as real once the IPC write actually
      // succeeded — a failed write never reached the pty, so it never
      // "started" anything. Also gated on `activationClaimed` (fix round 2 /
      // R1-a): if auto-launch (or an earlier click) already started this
      // agent in this session, the main process reports the slot as already
      // claimed and this click must not fire a second activation event.
      if (result?.success && result.activationClaimed) {
        track('feature:ai_terminal_agent_started', { agent: agent.id, source: 'button' });
      }
    } finally {
      writeInFlightRef.current = false;
    }
  };

  return (
    <div className="flex items-center gap-1">
      {agents.map((agent) => (
        <button
          key={agent.id}
          type="button"
          onClick={handleStart(agent)}
          data-testid={`ai-agent-start-btn-${agent.id}`}
          title={`Start ${agent.displayName}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium hover:bg-primary/20 transition-colors"
        >
          <Play className="h-3 w-3" />
          {agent.displayName}
        </button>
      ))}
    </div>
  );
}
